import {
  Box,
  BoxSx,
  Divider,
  Grid,
  GridColumn,
  Icon,
  Text
} from "@modernatx/ui-kit-react";
import React from "react";

import { InvoiceFields, InvoiceStatus, PaymentInfo } from "@/types/Invoice";
import { formatDate } from "@/utils/InvoicePay";

import DisplayPaymentInfo from "../Payments/DisplayPaymentInfo";

interface PaymentDetailsProps {
  payment: PaymentInfo | null;
  paymentStatus: InvoiceStatus;
}

const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  payment,
  paymentStatus
}) => {
  const headerBackground = React.useMemo(() => {
    return paymentStatus === InvoiceStatus.Paid
      ? "color-system-green-600"
      : "color-system-gray-600";
  }, [paymentStatus]);

  const headerContent = React.useMemo(() => {
    if (paymentStatus === InvoiceStatus.Paid) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "spacing-2" }}>
          <Icon icon="check-circle" size="xs" color="white" />
          {payment?.paymentDate
            ? `Paid on ${formatDate(payment.paymentDate)}`
            : "Paid"}
        </Box>
      );
    } else {
      return "Pending";
    }
  }, [paymentStatus, payment?.paymentDate]);

  const paidHeaderStyle: BoxSx = React.useMemo(() => {
    return paymentStatus === InvoiceStatus.Paid
      ? { display: "flex", alignItems: "center", gap: "spacing-2" }
      : {};
  }, [paymentStatus]);

  return (
    <Grid>
      <GridColumn>
        <Grid>
          <GridColumn
            sx={{
              backgroundColor: headerBackground,
              borderTopLeftRadius: "radius-2",
              borderTopRightRadius: "radius-2"
            }}
          >
            <Text
              variant="body-1-bold"
              sx={{
                color: "white",
                px: "spacing-6",
                py: "spacing-4",
                ...paidHeaderStyle
              }}
            >
              {headerContent}
            </Text>
          </GridColumn>
        </Grid>
        <Grid>
          <GridColumn>
            <Box
              sx={{
                borderLeft: "1px solid",
                borderBottom: "1px solid",
                borderRight: "1px solid",
                borderColor: "color-system-gray-300",
                borderBottomLeftRadius: "radius-2",
                borderBottomRightRadius: "radius-2",
                px: "spacing-6",
                pt: "spacing-4",
                pb: "spacing-6",
                color: "color-system-gray-600",
                width: "auto"
              }}
            >
              <Grid>
                <GridColumn>
                  <DisplayPaymentInfo
                    fieldName={InvoiceFields.ConfirmationNum}
                    fieldValue={payment?.confirmationNum || "Unavailable"}
                  />
                  <DisplayPaymentInfo
                    fieldName={InvoiceFields.PaymentDate}
                    fieldValue={payment?.paymentDate || "Unavailable"}
                  />
                  <DisplayPaymentInfo
                    fieldName={InvoiceFields.PaymentMethod}
                    fieldValue={payment?.paymentMethod || "Unavailable"}
                  />
                </GridColumn>
              </Grid>
              <Box sx={{ pt: "spacing-4", pb: "spacing-2" }}>
                <Divider color="color-system-gray-300" />
              </Box>
              <Grid>
                <GridColumn>
                  <DisplayPaymentInfo
                    fieldName="Amount paid"
                    fieldValue={payment?.amount || "Unavailable"}
                  />
                </GridColumn>
              </Grid>
            </Box>
          </GridColumn>
        </Grid>
      </GridColumn>
    </Grid>
  );
};

export default PaymentDetails;
